import { FacturaCFDIMovimientoPago, IFacturaCFDIConceptoFiscal as IFacturaCFDIConceptoFiscal_, IFacturaCFDIConceptosFiscalesProcesados as IFacturaCFDIConceptosFiscalesProcesados_, IPagoInfoFacturarConceptoComplemento } from "../../../../data/entidad/Factura";
import { _DICC_ALUMNO } from "../../../../data/modulo/Alumno";
import { _DICC_ESCOLARIDAD } from "../../../../data/modulo/Escolaridad";
import { _SvFacturaCFDIProcesarConceptosFiscales } from "../../../../data/modulo/FacturaCFDI";
import _L, { _HttpMsgV2 } from "../../../../util/Labels";
import { Tag } from "../../../components/Tag/Tag";
import { _InfoIcon } from '../../../controlD3/InfoIcon';
import { List } from "../../../controlD3/List";
import { ModalThingsUtil as MTUtil } from "../../../controlD3/ModalThings/Utils";
import { NotificacionV2 } from "../../../controlD3/NotificacionV2";
import { UIUtilElement } from "../../../util/Element";
import { UIUtilFormat } from "../../../util/Format";
import { AlumnoEdoCtaFacturacionUtil, AlumnoEdoCtaFacturacionUtil as Util } from "./FacturacionUtil";
import "./FacturacionViewMovs.scss";

export type IFacturaCFDIConceptoFiscalAdvanced = IFacturaCFDIConceptoFiscal_ & {
    /** IdAgrupador-IdsMovs: 5-1,2,3,4 */
    _Identificador?: string;
    _Complemento?: IPagoInfoFacturarConceptoComplemento;
}
export type IFacturaCFDIConceptosFiscalesProcesadosAdvanced = Omit<IFacturaCFDIConceptosFiscalesProcesados_, "ConceptosFiscales"> & {
    /** @override */
    ConceptosFiscales: IFacturaCFDIConceptoFiscalAdvanced[];
    /** Ayuda a identificar los movimientos asociados a este cfdi
     * @readonly
    */
    _IdsMovs?: number[];
    /** "CFDI" + index */
    _NombreCFDI?: string;
}

type IAlumnoEdoCtaFacturacionViewMovsConfig = {
    /** Contenedor HTML donde se renderizará la vista */
    container: TSelectionHTML<"div">
    /** Instancia del modal que contiene la vista */
    mt: MTUtil.IModalThings
    /** ID de la escuela asociada */
    idEscuela: number
    /** ID del alumno asociado */
    idAlumno: number
    /** IDs de los pagos a consultar */
    idsPagos: number[]
    curpAlumno: string
    onloadData?: (firstData: boolean, success: boolean) => void
}

/**
 * Clase que implementa las funcionalidades relacionadas con la vista de movimientos de pagos
 * para la facturación de un alumno.
 */
export class AlumnoEdoCtaFacturacionViewMovs implements ViewMovimientosPagos {
    private modal: MTUtil.IModalThings<any>;
    private config: IAlumnoEdoCtaFacturacionViewMovsConfig;
    private pagosInfoListCtrl: List<IFacturaCFDIConceptosFiscalesProcesadosAdvanced>;
    private sinRegistrosComponent: AlumnoEdoCtaFacturacionUtil.SinRegistrosComponent;
    private cfdisConceptosProcesadosList: IFacturaCFDIConceptosFiscalesProcesadosAdvanced[] = [];
    private lblSeleccionadoInfo: HTMLLabelElement & { _refreshText?: () => void };
    /** Solo contiene la info seleccionada */
    private dataSelected: {
        [grupoCFDI: string]: {
            [identificadorConcepto: string]: Set<number>
        }
    } = {};

    constructor(config: IAlumnoEdoCtaFacturacionViewMovsConfig) {
        this.modal = config.mt;
        this.config = config;

        const { container } = config;

        container
            .classed("alumnoedocuenta-facturacion-movs", true)
            .text(_L("panelfinanzasedocuenta_factura.movs_seleccionapagos"))
            .style("min-height", "200px")
            .append(() => {
                this.lblSeleccionadoInfo = UIUtilElement._CreateElementFromHTML(`<label class="lbl-seleccionado-info"></label>`);
                this.lblSeleccionadoInfo._refreshText = () => {
                    this.lblSeleccionadoInfo.textContent = this.GetMovsInfo();
                }
                return this.lblSeleccionadoInfo;
            })

        // Lista de CFDI y sus conceptos fiscales procesados
        this.pagosInfoListCtrl = Util._CreateListControl<IFacturaCFDIConceptosFiscalesProcesadosAdvanced>({ addMarginTop: true })
            ._SetParent(container)
            ._SetUpdateItem((container, cfdiC) => {
                container.html("") // Limpia contenido previo
                container.append(() => {
                    const tag = new Tag({ Value: cfdiC._NombreCFDI });
                    const cfdiIsSelected = !!this.dataSelected[cfdiC.GrupoCFDI];
                    if (cfdiIsSelected) tag._ControlContainer.classList.add("tag-selected");
                    tag._ControlContainer.onclick = () => {
                        this.SelectCFDIsByKeys(cfdiC.GrupoCFDI, !cfdiIsSelected);
                        this.RefreshView();
                    }
                    return tag._ControlContainer;
                })
                container.append(() => {
                    const tieneClaveFormaPago = !!cfdiC.ClaveFormaPagoSAT;
                    const cfdiDescription = UIUtilElement._CreateElementFromHTML(`<div>
                        <div name="cfdi-forma-pago-div">
                            <b>${_L("panelfinanzasedocuenta_factura.forma_pago")}:</b>
                            ${tieneClaveFormaPago ? `${cfdiC.ClaveFormaPagoSAT} - ${cfdiC.NombreFormaPagoSAT}` : _L("general.por_definir")}
                        </div>
                        <div>
                            <b>${_L("panelfinanzasedocuenta_factura.fecha_pago")}:</b>
                            ${cfdiC.FechaPagoFmt}
                        </div>
                    </div>`);
                    if (!tieneClaveFormaPago) {
                        _InfoIcon(cfdiDescription.querySelector("div[name='cfdi-forma-pago-div']") as HTMLElement, _L("panelfinanzasedocuenta_factura.tooltip_info_definir"));
                    }
                    return cfdiDescription;
                })
                container.append(() => {
                    // Lista de conceptos fiscales
                    const conceptosList = UIUtilElement._CreateElementFromHTML<HTMLDivElement>(`<div><b>${_L("panelfinanzasedocuenta_factura.conceptos")}:</b></div>`);
                    new List<IFacturaCFDIConceptoFiscalAdvanced>()
                        ._SetParent(conceptosList)
                        ._SetUpdateItem((itemDivConcepto, conceptoF) => {
                            const conceptoIsSelected = !!this.dataSelected[cfdiC.GrupoCFDI] && !!this.dataSelected[cfdiC.GrupoCFDI][conceptoF._Identificador];
                            const conceptoContent = itemDivConcepto.append("div");
                            const tieneClaveProdServ = !!conceptoF.ClaveProdServSAT;
                            const tieneClaveUnidad = !!conceptoF.ClaveUnidadSAT;
                            const conceptoHtml = `
                                <div class="item">${conceptoF.Descripcion}</div>
                                ${!conceptoF.EsFacturable
                                    ? `<div style="color: var(--color_app_red1);">${_L("panelfinanzasedocuenta_factura.no_facturable")}</div>`
                                    : ""
                                }
                                <div>
                                    <i name="claveprodserv-div">
                                        ${_L("panelfinanzasedocuenta_factura.claveprodserv")}:
                                        ${tieneClaveProdServ ? `${conceptoF.ClaveProdServSAT} - ${conceptoF.NombreProdServSAT}` : _L("general.por_definir")}
                                    </i>
                                </div>
                                <div>
                                    <i name="claveunidad-div">
                                        ${_L("panelfinanzasedocuenta_factura.unidad")}:
                                        ${tieneClaveUnidad ? `${conceptoF.ClaveUnidadSAT} - ${conceptoF.NombreUnidadSAT}` : _L("general.por_definir")}
                                    </i>
                                </div>
                            `;
                            Util._ViewItemListCheck(conceptoContent, conceptoIsSelected, conceptoHtml)
                                .call((section) => {
                                    if (!tieneClaveProdServ) {
                                        console.warn("nodo claveprodserv-div", section.select("i[name='claveprodserv-div']").node());
                                        _InfoIcon(section.select("i[name='claveprodserv-div']").node() as HTMLElement, _L("panelfinanzasedocuenta_factura.tooltip_info_definir"));
                                    }
                                    if (!tieneClaveUnidad) {
                                        _InfoIcon(section.select("i[name='claveunidad-div']").node() as HTMLElement, _L("panelfinanzasedocuenta_factura.tooltip_info_definir"));
                                    }
                                })
                                .node().onclick = () => {
                                    this.SelectConceptosByKeys(cfdiC.GrupoCFDI, conceptoF._Identificador, !conceptoIsSelected);
                                    this.RefreshView();
                                }

                            // Detalles de los movimientos asociados al concepto
                            if (conceptoF.MovimientoFinanza?.length) {
                                const movsAgrupadosSection = itemDivConcepto.append("div").attr("class", "movimientos-agrupados-section");
                                movsAgrupadosSection.append("div").append("b").text(_L("panelfinanzasedocuenta_factura.movs_asociados_a_concepto"));
                                new List<FacturaCFDIMovimientoPago>()
                                    ._SetParent(movsAgrupadosSection)
                                    ._SetUpdateItem((itemDivMov, movPago) => {
                                        const movIsSelected = conceptoIsSelected && this.dataSelected[cfdiC.GrupoCFDI][conceptoF._Identificador]?.has(movPago.Id);
                                        const movContent = itemDivMov.append("div");
                                        const movHtml = `
                                            <div class="item">${movPago.NombreCargo}</div>
                                            ${!movPago.EsFacturable
                                                ? `<div style="color: var(--color_app_red1);">${_L("panelfinanzasedocuenta_factura.no_facturable")}</div>`
                                                : ""
                                            }
                                            <div><i>${_L("panelfinanzasedocuenta_factura.periodo")}: ${movPago.NombrePeriodo}</i></div>
                                            <div><i>${_L("panelfinanzasedocuenta_factura.monto")}: ${UIUtilFormat._CurrencyFmt(movPago.Monto)}</i></div>
                                        `;
                                        Util._ViewItemListCheck(movContent, movIsSelected, movHtml)
                                            .node().onclick = () => {
                                                this.SelectMovsByKeys(cfdiC.GrupoCFDI, conceptoF._Identificador, movPago.Id, movPago.EsFacturable, !movIsSelected);
                                                this.RefreshView();
                                            }
                                    })
                                    ._SetItems(conceptoF.MovimientoFinanza)
                                    ._ListContainerSelection.classed("list-movimientos-pagos", true)
                            }
                        })
                        ._SetItems(cfdiC.ConceptosFiscales)
                        ._ListContainerSelection.classed("list-conceptos-cfdi", true)
                    return conceptosList;
                })
            })
        this.pagosInfoListCtrl._ListContainerSelection.classed("list-cfdis", true)

        /** Componente para mostrar un mensaje cuando no hay registros */
        this.sinRegistrosComponent = Util._CreateSinRegistrosComponent(container)

        this.ConsultaMovimientosProcesar() // Realiza la consulta inicial
    }

    // ****************************************************************
    // PUBLIC METHODS
    // ****************************************************************

    //#region PUBLIC METHODS

    /** Obtiene la cantidad de CFDIs (pre-procesados) seleccionados */
    public GetCFDISelectedCount(): number {
        return Object.keys(this.dataSelected).length;
    }

    /** Re-consulta los conceptos fiscales procesados de los CFDIs seleccionados */
    public ConsultaCFDIConceptosFiscalesProcesados(): Promise<IFacturaCFDIConceptosFiscalesProcesadosAdvanced[]> {
        const idsMovsSelected = this.GetIdsMovsSelected();
        return _SvFacturaCFDIProcesarConceptosFiscales(this.config.idEscuela, this.config.idAlumno, idsMovsSelected)
            .then<IFacturaCFDIConceptosFiscalesProcesadosAdvanced[]>(res => {
                return (res.Datos || []).map((cfdiC, i) => (
                    this.CompletaCFDIConceptos(cfdiC, (i + 1).toString())
                ));
            })
        /*
        const cfdiConceptosSeleccionados: IFacturaCFDIConceptosFiscalesProcesadosAdvanced[] = [];
        for (const cfdiConceptos of this.cfdisConceptosProcesadosList) {
            if (!this.dataSelected[cfdiConceptos.GrupoCFDI]) {
                continue;
            }
            const cfdiConceptosFinal: IFacturaCFDIConceptosFiscalesProcesadosAdvanced = {
                ...cfdiConceptos,
                ConceptosFiscales: [] as IFacturaCFDIConceptoFiscalAdvanced[],
                _IdsMovs: [] as number[],
            };
            cfdiConceptosSeleccionados.push(cfdiConceptosFinal);

            for (const concepto of cfdiConceptos.ConceptosFiscales) {
                if (!this.dataSelected[cfdiConceptos.GrupoCFDI][concepto._Identificador]) {
                    continue;
                }
                const conceptoFinal = { ...concepto, MovimientoFinanza: [] as FacturaCFDIMovimientoPago[] };
                cfdiConceptosFinal.ConceptosFiscales.push(conceptoFinal);

                for (const mov of concepto.MovimientoFinanza) {
                    if (this.dataSelected[cfdiConceptos.GrupoCFDI][concepto._Identificador].has(mov.Id)) {
                        const movFinal = { ...mov };
                        conceptoFinal.MovimientoFinanza.push(movFinal);
                        cfdiConceptosFinal._IdsMovs.push(movFinal.Id);
                    }
                }
            }
        }
        return cfdiConceptosSeleccionados
        */
    }

    public IsValid(): boolean {
        const isValid = Object.keys(this.dataSelected).length > 0;
        if (!isValid)
            NotificacionV2._Mostrar(_L("panelfinanzasedocuenta_factura.movs_seleccionapagos"), "ADVERTENCIA")
        return isValid
    }

    public SelectMovs(ids: number[]): this {
        this.SelectMovsByIds(ids, true);
        this.RefreshView();
        return this;
    }

    public SelectAll(): this {
        this.SelectAllMovs(true);
        this.RefreshView();
        return this;
    }

    public ReloadData(): this {
        this.ConsultaMovimientosProcesar();
        return this;
    }

    public RefreshView(): this {
        this.pagosInfoListCtrl._RefreshList();
        this.lblSeleccionadoInfo._refreshText();
        return this;
    }

    //#endregion

    // ****************************************************************
    // PRIVATE METHODS
    // ****************************************************************

    //#region PRIVATE METHODS

    private GetMovsInfo(): string {
        let nCFDIs = 0;
        let nConceptos = 0;
        let nMovs = 0;
        for (const grupoCFDI in this.dataSelected) {
            nCFDIs++;
            for (const identificadorConcepto in this.dataSelected[grupoCFDI]) {
                nConceptos++;
                nMovs += this.dataSelected[grupoCFDI][identificadorConcepto].size;
            }
        }
        return _L("panelfinanzasedocuenta_factura.movs_seleccionados_info", nCFDIs, nMovs, nConceptos)
    }

    private GetIdsMovsSelected(): number[] {
        const ids: number[] = [];
        for (const grupoCFDI in this.dataSelected) {
            for (const identificadorConcepto in this.dataSelected[grupoCFDI]) {
                ids.push(...this.dataSelected[grupoCFDI][identificadorConcepto]);
            }
        }
        return ids;
    }

    private GetMovInfo(idMov: number): { grupoCFDI: string, identificadorConcepto: string, esFacturable: boolean } | null {
        for (const cfdiconceptos of this.cfdisConceptosProcesadosList) {
            for (let i = 0; i < cfdiconceptos.ConceptosFiscales.length; i++) {
                const c = cfdiconceptos.ConceptosFiscales[i];
                const mov = c.MovimientoFinanza.find(mov => mov.Id == idMov);
                if (mov) {
                    return { grupoCFDI: cfdiconceptos.GrupoCFDI, identificadorConcepto: c._Identificador, esFacturable: mov.EsFacturable }
                }
            }
        }
        return null
    }

    private SelectCFDIsByKeys(grupoCFDI: string, selected: boolean) {
        if (!this.dataSelected[grupoCFDI] && !selected) return;
        if (selected) {
            const cfdiConceptos: IFacturaCFDIConceptosFiscalesProcesadosAdvanced = this.cfdisConceptosProcesadosList.find(c => c.GrupoCFDI == grupoCFDI);
            if (!cfdiConceptos) return;
            cfdiConceptos.ConceptosFiscales.forEach(concepto => {
                concepto.MovimientoFinanza.forEach(mov => {
                    this.SelectMovsByKeys(grupoCFDI, concepto._Identificador, mov.Id, mov.EsFacturable, true);
                });
            });
        } else {
            delete this.dataSelected[grupoCFDI];
            // console.warn("dataSelected", this.dataSelected, selected, grupoCFDI, "ALL", "ALL");
        }
    }

    private SelectConceptosByKeys(grupoCFDI: string, identificadorConcepto: string, selected: boolean) {
        if (!this.dataSelected[grupoCFDI]?.[identificadorConcepto] && !selected) return;
        if (selected) {
            const cfdiConceptos: IFacturaCFDIConceptosFiscalesProcesadosAdvanced = this.cfdisConceptosProcesadosList.find(c => c.GrupoCFDI == grupoCFDI);
            if (!cfdiConceptos) return;
            const concepto: IFacturaCFDIConceptoFiscalAdvanced = cfdiConceptos.ConceptosFiscales.find(c => c._Identificador == identificadorConcepto);
            if (!concepto) return;
            concepto.MovimientoFinanza.forEach(mov => {
                this.SelectMovsByKeys(grupoCFDI, identificadorConcepto, mov.Id, mov.EsFacturable, true);
            });
        } else {
            if (this.dataSelected[grupoCFDI] && this.dataSelected[grupoCFDI][identificadorConcepto]) {
                delete this.dataSelected[grupoCFDI][identificadorConcepto];
                if (!Object.keys(this.dataSelected[grupoCFDI]).length)
                    delete this.dataSelected[grupoCFDI];
                // console.warn("dataSelected", this.dataSelected, selected, grupoCFDI, identificadorConcepto, "ALL");
            }
        }
    }

    /**
     * Selecciona o deselecciona un movimiento específico utilizando claves compuestas.
     * @param grupoCFDI Grupo CFDI al que pertenece el movimiento.
     * @param identificadorConcepto Identificador del concepto.
     * @param idMov ID del movimiento a seleccionar o deseleccionar.
     * @param esFacturable Indica si el movimiento es facturable.
     * @param selected Indica si se debe seleccionar (true) o deseleccionar (false) el movimiento.
     */
    private SelectMovsByKeys(grupoCFDI: string, identificadorConcepto: string, idMov: number, esFacturable: boolean, selected: boolean) {
        if (selected) {
            if (esFacturable) {
                if (!this.dataSelected[grupoCFDI])
                    this.dataSelected[grupoCFDI] = {};
                if (!this.dataSelected[grupoCFDI][identificadorConcepto])
                    this.dataSelected[grupoCFDI][identificadorConcepto] = new Set();
                this.dataSelected[grupoCFDI][identificadorConcepto].add(idMov);
            }
        } else {
            if (this.dataSelected[grupoCFDI] && this.dataSelected[grupoCFDI][identificadorConcepto]) {
                this.dataSelected[grupoCFDI][identificadorConcepto].delete(idMov);
                if (!this.dataSelected[grupoCFDI][identificadorConcepto].size)
                    delete this.dataSelected[grupoCFDI][identificadorConcepto];
                if (!Object.keys(this.dataSelected[grupoCFDI]).length)
                    delete this.dataSelected[grupoCFDI];
            }
        }
        // console.warn("dataSelected", this.dataSelected, selected, grupoCFDI, identificadorConcepto, idMov);
    }

    private SelectMovsByIds(idsMovs: number[], selected: boolean) {
        idsMovs.forEach(idMov => {
            const movInfo = this.GetMovInfo(idMov);
            if (!movInfo) return;
            const { grupoCFDI, identificadorConcepto, esFacturable } = movInfo;
            this.SelectMovsByKeys(grupoCFDI, identificadorConcepto, idMov, esFacturable, selected);
        });
    }

    private SelectAllMovs(selected: boolean) {
        if (!selected) {
            this.dataSelected = {};
            return;
        }
        this.cfdisConceptosProcesadosList.forEach(cfdiconceptos => {
            cfdiconceptos.ConceptosFiscales.forEach((concepto) => {
                concepto.MovimientoFinanza.forEach(mov => {
                    if (mov.EsFacturable) {
                        if (!this.dataSelected[cfdiconceptos.GrupoCFDI])
                            this.dataSelected[cfdiconceptos.GrupoCFDI] = {};
                        if (!this.dataSelected[cfdiconceptos.GrupoCFDI][concepto._Identificador])
                            this.dataSelected[cfdiconceptos.GrupoCFDI][concepto._Identificador] = new Set();
                        this.dataSelected[cfdiconceptos.GrupoCFDI][concepto._Identificador].add(mov.Id);
                    }
                });
            });
        });
        // console.warn("dataSelected", this.dataSelected, selected, "ALL");
    }

    /** Obtiene el complemento IEDU de un concepto fiscal */
    private GetComplementoConcepto(grupoCFDI: string, identificadorConcepto: string): IPagoInfoFacturarConceptoComplemento | null {
        const { idAlumno, curpAlumno } = this.config;
        const alumno = _DICC_ALUMNO.get(idAlumno);

        const complementoExistente = this.cfdisConceptosProcesadosList.length
            ? (() => {
                const cfdiPrevio = this.cfdisConceptosProcesadosList.find(c => c.GrupoCFDI == grupoCFDI);
                if (!cfdiPrevio) return null;
                const conceptoPrevio = cfdiPrevio.ConceptosFiscales.find(c => c._Identificador == identificadorConcepto);
                return conceptoPrevio?._Complemento || null;
            })()
            : null;
        return complementoExistente || {
            NombreAlumno: alumno.NombreCompleto,
            CURP: curpAlumno,
            NivelEducativo: _DICC_ESCOLARIDAD.get(alumno.IdEscolaridad).NivelSAT,
            AutRVOE: _DICC_ESCOLARIDAD.get(alumno.IdEscolaridad).AutRVOE,
            RFCPago: ""
        };
    }

    /** Completa la información de un concepto fiscal
     * @param cfdiC Conceptos fiscales procesados a completar
     * @param sufixCfdiName Sufijo para el nombre del CFDI (normalmente un índice: 1, 2, 3, ...)
     * @returns El mismo objeto `cfdiC` pero con las propiedades adicionales completadas
     */
    private CompletaCFDIConceptos(cfdiC: IFacturaCFDIConceptosFiscalesProcesadosAdvanced, sufixCfdiName: string): IFacturaCFDIConceptosFiscalesProcesadosAdvanced {
        cfdiC._NombreCFDI = `${_L("panelfinanzasedocuenta_factura.cfdi")} ${sufixCfdiName}`;
        cfdiC._IdsMovs = [];
        cfdiC.ConceptosFiscales.forEach((conceptoF: IFacturaCFDIConceptoFiscalAdvanced) => {
            conceptoF._Complemento = this.GetComplementoConcepto(cfdiC.GrupoCFDI, conceptoF._Identificador);
            conceptoF._Identificador = `${conceptoF.IdAgrupador}-${conceptoF.MovimientoFinanza.map(m => m.Id).join(",")}`;
            conceptoF.MovimientoFinanza.forEach(mov => {
                cfdiC._IdsMovs.push(mov.Id);
            });
        });
        return cfdiC;
    }

    //#endregion

    // ****************************************************************
    // SERVICE CALLS
    // ****************************************************************

    //#region SERVICE CALLS - CONSULTAS Y PROCESAMIENTO DE DATOS

    private ConsultaMovimientosProcesar() {
        const { idEscuela, idAlumno, idsPagos, curpAlumno } = this.config;
        const idsMovsSelectedAux = { ...this.dataSelected }
        this.dataSelected = {};

        const firstData = !this.cfdisConceptosProcesadosList.length;

        this.modal.Progress.node()._Visible = true

        _SvFacturaCFDIProcesarConceptosFiscales(idEscuela, idAlumno, idsPagos)
            .then((res) => {
                // Evalua respuesta
                if (res.Resultado <= 0) {
                    res.Mensaje = _HttpMsgV2(res)
                    NotificacionV2._Mostrar(res.Mensaje, "ADVERTENCIA")
                } else if (!res.Datos.length) {
                    res.Mensaje = _L("panelfinanzasedocuenta_factura.no_facturables")
                }
                if (res.Mensaje) {
                    this.sinRegistrosComponent._SetText(res.Mensaje)
                }
                this.sinRegistrosComponent._Visible(!!res.Mensaje)

                // Procesa datos de respuesta
                this.cfdisConceptosProcesadosList = (res.Datos || [])
                    .map((cfdiConceptos: IFacturaCFDIConceptosFiscalesProcesadosAdvanced, i: number) => {
                        this.CompletaCFDIConceptos(cfdiConceptos, (i + 1).toString());
                        cfdiConceptos.ConceptosFiscales.forEach((conceptoF: IFacturaCFDIConceptoFiscalAdvanced) => {
                            conceptoF.MovimientoFinanza.forEach(mov => {
                                if (!mov.EsFacturable) return;
                                if (idsMovsSelectedAux[cfdiConceptos.GrupoCFDI] && idsMovsSelectedAux[cfdiConceptos.GrupoCFDI][conceptoF._Identificador]?.has(mov.Id)) {
                                    this.SelectMovsByKeys(cfdiConceptos.GrupoCFDI, conceptoF._Identificador, mov.Id, mov.EsFacturable, true);
                                }
                            });
                        });
                        return cfdiConceptos;
                    });
                this.pagosInfoListCtrl._SetItems(this.cfdisConceptosProcesadosList);
                this.lblSeleccionadoInfo._refreshText();

                if (this.config.onloadData) {
                    const success: boolean = res.Resultado > 0 && !!res.Datos?.length;
                    this.config.onloadData(firstData, success);
                }

                return !res.Mensaje
            })
            .finally(() => {
                this.modal.Progress.node()._Visible = false
            });
    }
}

export interface ViewMovimientosPagos {
    /** Consulta los conceptos fiscales procesados de los movimientos seleccionados */
    ConsultaCFDIConceptosFiscalesProcesados: () => Promise<IFacturaCFDIConceptosFiscalesProcesadosAdvanced[]>
    /** Valida si hay movimientos seleccionados */
    IsValid: () => boolean
    /** Selecciona movimientos específicos por sus IDs */
    SelectMovs: (ids: number[]) => void
    /** Selecciona todos los movimientos facturables */
    SelectAll: () => void
    // /** Promesa que indica si la carga de datos se completó */
    // LoadComplete: Promise<boolean>
    /** Refresca la información de los movimientos */
    ReloadData: () => void
}